import Cookies from "js-cookie";
const BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.didarr.ir";
class AuthApi {
    isRefreshing = false;
    refreshPromise = null;
    getAuthHeaders() {
        const token = Cookies.get("accessToken");
        return {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
    }
    async refreshAccessToken() {
        const refreshToken = Cookies.get("refreshToken");
        if (!refreshToken) {
            throw new Error("No refresh token available");
        }
        const response = await fetch(`${BASE_URL}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) {
            throw new Error("Failed to refresh token");
        }
        const data = await response.json();
        this.saveTokens(data.accessToken, data.refreshToken);
        return data;
    }
    async fetchWithAutoRefresh(url, options) {
        let response = await fetch(url, { ...options, headers: this.getAuthHeaders() });
        if (response.status === 401) {
            if (!this.isRefreshing) {
                this.isRefreshing = true;
                this.refreshPromise = this.refreshAccessToken();
            }
            try {
                await this.refreshPromise;
                this.isRefreshing = false;
                this.refreshPromise = null;
                response = await fetch(url, { ...options, headers: this.getAuthHeaders() });
            }
            catch {
                this.isRefreshing = false;
                this.refreshPromise = null;
                throw new Error("Request failed");
            }
        }
        if (!response.ok) {
            // Try to parse error response body
            try {
                const errorData = await response.json();
                const error = new Error(errorData.message || `Request failed with status ${response.status}`);
                error.status = response.status;
                error.data = errorData;
                throw error;
            }
            catch (parseError) {
                // If parsing fails, throw generic error
                const error = new Error(`Request failed with status ${response.status}`);
                error.status = response.status;
                throw error;
            }
        }
        return response.json();
    }
    async requestOtp(phoneNumber) {
        const response = await fetch(`${BASE_URL}/auth/otp/request`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phoneNumber }),
        });
        if (!response.ok) {
            throw new Error("Failed to request OTP");
        }
        return response.json();
    }
    async verifyOtp(phoneNumber, otp) {
        const response = await fetch(`${BASE_URL}/auth/otp/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phoneNumber, otp }),
        });
        if (!response.ok) {
            throw new Error("Failed to verify OTP");
        }
        return response.json();
    }
    async getMe() {
        return this.fetchWithAutoRefresh(`${BASE_URL}/me`, { method: "GET" });
    }
    async setUsername(username) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/auth/username`, {
            method: "POST",
            body: JSON.stringify({ username }),
        });
    }
    async generateNameRoom() {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/generate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        });
    }
    async enterRoom(roomName, deviceId) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/enter`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ roomName, deviceId }),
        });
    }
    async exitRoom(roomName, deviceId) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/exit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ roomName, deviceId }),
        });
    }
    async exitAll(roomName) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/exitAll`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ roomName }),
        });
    }
    async checkRoomName(roomName) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/check/${roomName}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
    }
    async submitSupportForm(data) {
        const response = await fetch(`${BASE_URL}/support`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (response.status === 429) {
            throw new Error("TOO_MANY_REQUESTS");
        }
        if (!response.ok) {
            throw new Error("Failed to submit support form");
        }
        return response.json();
    }
    async getMyTickets(phoneNumber) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/support/my-tickets?phone=${encodeURIComponent(phoneNumber)}`, { method: "GET" });
    }
    async lookupTicket(referenceCode) {
        const response = await fetch(`${BASE_URL}/support/lookup?code=${encodeURIComponent(referenceCode)}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) {
            throw new Error("Failed to lookup ticket");
        }
        return response.json();
    }
    async getTicketThread(accessToken) {
        const response = await fetch(`${BASE_URL}/support/thread?token=${encodeURIComponent(accessToken)}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) {
            throw new Error("Failed to get ticket thread");
        }
        return response.json();
    }
    async replyToTicket(accessToken, message) {
        const response = await fetch(`${BASE_URL}/support/thread/reply?token=${encodeURIComponent(accessToken)}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message }),
        });
        if (response.status === 400) {
            const error = await response.json();
            throw new Error(error.message || "Ticket is closed");
        }
        if (!response.ok) {
            throw new Error("Failed to reply to ticket");
        }
        return response.json();
    }
    async sendInvite(data) {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/${data.roomName}/invite`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phoneNumbers: data.phoneNumbers }),
        });
    }
    async activeRoom() {
        return this.fetchWithAutoRefresh(`${BASE_URL}/rooms/active`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
    }
    saveTokens(accessToken, refreshToken) {
        // Save tokens in cookies with 30 days expiry
        Cookies.set("accessToken", accessToken, { expires: 30, secure: true, sameSite: "strict" });
        Cookies.set("refreshToken", refreshToken, { expires: 30, secure: true, sameSite: "strict" });
    }
    saveUserInfo(userInfo) {
        // Save user info in cookies with 30 days expiry
        Cookies.set("userInfo", JSON.stringify(userInfo), { expires: 30, secure: true, sameSite: "strict" });
    }
    getUserInfo() {
        const userInfoStr = Cookies.get("userInfo");
        if (!userInfoStr) {
            return null;
        }
        try {
            return JSON.parse(userInfoStr);
        }
        catch {
            return null;
        }
    }
    getAccessToken() {
        return Cookies.get("accessToken") || null;
    }
    clearTokens() {
        Cookies.remove("accessToken");
        Cookies.remove("refreshToken");
        Cookies.remove("userInfo");
    }
    isAuthenticated() {
        return !!this.getAccessToken();
    }
    async logout() {
        const refreshToken = Cookies.get("refreshToken");
        if (refreshToken) {
            try {
                await fetch(`${BASE_URL}/auth/logout`, {
                    method: "POST",
                    headers: this.getAuthHeaders(),
                    body: JSON.stringify({ refreshToken }),
                });
            }
            catch {
                // Ignore logout errors
            }
        }
        this.clearTokens();
    }
}
export const authApi = new AuthApi();
